/**
 * The `next-step` forward affordance (UI/UX plan §2, ruling D4; R5 2026-07-29) —
 * ONE consistent, quietly-styled step every workflow surface ends with: a small
 * »Nächster Schritt« eyebrow, the German label, and a forward arrow pinned to the
 * row's RIGHT EDGE. It is deliberately SUBORDINATE to a hub's hero CTA where both
 * appear (a hero drives; this marks the onward path).
 *
 * **There is exactly ONE implementation: the `<career-next-step>` custom element.**
 * Six surfaces call {@link nextStep}, which is now a thin ADAPTER that emits the
 * tag — so every call site keeps working untouched and no second copy of the
 * markup can drift into existence. New code may write the tag directly.
 *
 * Two states, one shape ({@link NextStep}):
 *   - unlocked → an `<a data-act="nav" data-url=…>` (the host surface's
 *     `actions().nav` dispatches it; `nav` is a registered AX verb carrying the
 *     url payload — the element renders light DOM precisely so the host's ONE
 *     delegated listener still sees the click);
 *   - locked → a non-interactive block carrying the honest German reason (AX-10:
 *     gated ≠ broken), styled with the REGISTERED `is-locked` state class.
 *
 * Why a PLAIN custom element and not a `KitElement`: this is a leaf with no
 * ViewModel, no injected services and no signals — its whole state is four
 * attributes. A KitElement would additionally route every render through the
 * scheduler, so the element's DOM would land one flush LATER than its host's,
 * making the surface goldens depend on flush counts. Rendering synchronously in
 * `connectedCallback` / `attributeChangedCallback` keeps a next-step present the
 * moment its host renders. Light DOM throughout — this app styles globally from
 * `index.html` and has no shadow-DOM styling anywhere.
 */

import { html, ifDefined, litRender, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { NextStep } from "../domain"
import { GLYPH } from "./icons"

/** The forward arrow glyph (matches the Kompass-row chevron). */
const ARROW = html`<svg class="next-step-arrow" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.chevronRight}</svg>`
const LOCK = html`<svg class="next-step-lock" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 10V8a6 6 0 0 1 12 0v2" /><rect x="4" y="10" width="16" height="10" rx="2" /></svg>`

/** Lazily-resolved base so importing this module never requires DOM globals (web-kit's idiom). */
const HTMLElementBase: typeof HTMLElement =
  (globalThis as { HTMLElement?: typeof HTMLElement }).HTMLElement ??
  (class {
    constructor() {
      throw new Error("[career] <career-next-step> requires a DOM environment (HTMLElement is not defined).")
    }
  } as unknown as typeof HTMLElement)

/**
 * `<career-next-step label url eyebrow locked-reason>` — THE forward affordance.
 *
 * Attributes (all plain, none `data-*`: the payload the delegated action listener
 * reads sits on the inner `<a data-act="nav">`, where AX-3 wants it):
 *   - `label`         — the German forward label, already resolved through `t()`;
 *   - `url`           — the target route (English code, AX-9);
 *   - `eyebrow`       — the resolved »Nächster Schritt« label;
 *   - `locked-reason` — present ⇒ LOCKED, and this is the honest German reason.
 */
export class CareerNextStep extends HTMLElementBase {
  static readonly observedAttributes: readonly string[] = ["label", "url", "eyebrow", "locked-reason"]

  #connected = false

  connectedCallback(): void {
    this.#connected = true
    this.#render()
  }

  disconnectedCallback(): void {
    this.#connected = false
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || !this.#connected) return
    this.#render()
  }

  #render(): void {
    litRender(this.#template(), this)
  }

  #template(): TemplateResult {
    const label = this.getAttribute("label") ?? ""
    const url = this.getAttribute("url") ?? ""
    const eyebrow = this.getAttribute("eyebrow") ?? ""
    const reason = this.getAttribute("locked-reason")
    const locked = reason !== null
    const inner = html`
      <span class="next-step-eyebrow eyebrow" lang="de">${eyebrow}</span>
      <span class="next-step-body">
        <span class="next-step-label" lang="de">${label}</span>
        ${locked ? LOCK : ARROW}
      </span>
      ${locked ? html`<span class="next-step-reason" lang="de">${reason}</span>` : nothing}
    `
    if (locked) return html`<div class="next-step is-locked" aria-disabled="true">${inner}</div>`
    return html`<a class="next-step" href=${url} data-act="nav" data-url=${url} lang="de">${inner}</a>`
  }
}

/**
 * Adapter: render the golden-path forward affordance for a surface. `eyebrow` is the
 * resolved »Nächster Schritt« label (kept a parameter so copy stays in `t()`). An
 * absent `step` renders nothing (a surface with no onward step, e.g. the terminal).
 *
 * This emits {@link CareerNextStep}; it holds NO markup of its own on purpose.
 */
export function nextStep(eyebrow: string, step: NextStep | null | undefined): TemplateResult | typeof nothing {
  if (!step) return nothing
  return html`<career-next-step
    eyebrow=${eyebrow}
    label=${step.label}
    url=${step.url}
    locked-reason=${ifDefined(step.lockedReason)}
  ></career-next-step>`
}
